import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Container,
  Link,
  Avatar
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Business,
  Security,
  Description,
  VerifiedUser
} from '@mui/icons-material';
import { authApi } from '../../services/api.ts';
import config from '../../config/env.config.js';

interface LoginProps {
  onLoginSuccess: (userData: any) => void;
  onShowRegister?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess, onShowRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Por favor completa todos los campos');
      return;
    }

    if (!formData.email.includes('@')) {
      setError('Por favor ingresa un email válido');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Intentando login con:', formData);
      console.log('🔗 API URL:', config.API_URL);
      console.log('🔗 Process env REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
      console.log('🔗 Config completo:', config);
      
      const response = await authApi.login(formData);
      
      console.log('✅ Respuesta del servidor:', response.data);
      
      if (response.data && response.data.success) {
        try {
          localStorage.setItem('authToken', response.data.data.token);
          localStorage.setItem('userData', JSON.stringify(response.data.data.user));
          onLoginSuccess(response.data.data.user);
          setFormData({ email: '', password: '' });
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
          setError('Error al guardar datos de sesión. Intenta nuevamente.');
        }
      }
    } catch (err: any) {
      console.error('❌ Error en login:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error message:', err.message);
      setError(err.response?.data?.message || 'Error al iniciar sesión. Verifica tus credenciales.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Lado Izquierdo - Formulario */}
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          p: 4
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            {/* Header del formulario */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
                Bienvenido
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Ingresa tus credenciales para acceder al sistema
              </Typography>
            </Box>

            {/* Formulario */}
            <Paper elevation={0} sx={{ p: 0, backgroundColor: 'transparent' }}>
              <Box component="form" onSubmit={handleSubmit}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Iniciar Sesión'
                  )}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    o
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ¿No tienes una cuenta?
                  </Typography>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={onShowRegister}
                    sx={{
                      textDecoration: 'none',
                      fontWeight: 600,
                      color: '#1976d2',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Crear cuenta
                  </Link>
                </Box>
              </Box>
            </Paper>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                <Security sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                Sistema seguro con encriptación SSL
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Lado Derecho - Marca */}
      <Box 
        sx={{ 
          flex: 1, 
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Elementos decorativos */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
          }}
        />

        {/* Contenido de marca */}
        <Box sx={{ textAlign: 'center', color: 'white', zIndex: 1, position: 'relative' }}>
          <Avatar
            sx={{
              width: 120,
              height: 120,
              mx: 'auto',
              mb: 3,
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <Business sx={{ fontSize: 60 }} />
          </Avatar>
          
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Dienst
          </Typography>
          
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, opacity: 0.9 }}>
            Firma de Documentos
          </Typography>
          
          <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 400, mx: 'auto', mb: 4 }}>
            Sistema profesional de gestión de firmas electrónicas. 
            Simplifica tus procesos de documentación con tecnología avanzada.
          </Typography>

          {/* Características */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifiedUser sx={{ fontSize: 20 }} />
              <Typography variant="body2">Firmas legalmente válidas</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description sx={{ fontSize: 20 }} />
              <Typography variant="body2">Gestión documental avanzada</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security sx={{ fontSize: 20 }} />
              <Typography variant="body2">Seguridad de nivel empresarial</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
